import Close from "@mui/icons-material/Close";
import {
  Box,
  Button,
  Checkbox,
  DialogContent,
  Divider,
  FormControlLabel,
  IconButton,
  Stack,
  Typography,
} from "@mui/material";
import { SafeDialog } from "components/Dialog";
import { ReactNode } from "react";

type AnnouncementModalProps = {
  open: boolean;
  /** The permanent path — the announcement never shows again. */
  onDismiss: () => void;
  /** With `dontShowAgainLabel`, close is soft by default — `onDismiss` only fires once the checkbox is checked. The primary CTA is never affected; it always calls `onPrimaryCta` regardless of the checkbox. */
  onSoftDismiss?: () => void;
  dontShowAgainLabel?: string;
  /** Controlled checkbox state, owned by the caller (typically the `useProductAnnouncement` hook) — so anything else that can exit this announcement (e.g. a tour) can honor the same choice. Defaults to false when `dontShowAgainLabel` is omitted. */
  dontShowAgain?: boolean;
  onDontShowAgainChange?: (checked: boolean) => void;
  /** Optional hint shown under the checkbox, naming where the announcement can be reopened. */
  reopenHintText?: string;
  /** Icon or illustration in the left column, beside the title. */
  hero?: ReactNode;
  title: string;
  subtitle?: string;
  /** Body content — a paragraph, an illustration row, whatever the copy needs. */
  children?: ReactNode;
  primaryCtaLabel: string;
  onPrimaryCta: () => void;
  primaryCtaIcon?: ReactNode;
  secondaryCtaLabel?: string;
  secondaryCtaIcon?: ReactNode;
  onSecondaryCta?: () => void;
  /** Used for aria-labelledby and to key the title element. */
  titleId: string;
  dismissAriaLabel: string;
};

/**
 * Shared shell for one-time product announcement modals: a left-aligned
 * "what's new" card with the art in a left column, copy and an optional
 * support link in the right, and a footer row carrying the "don't show this
 * again" choice and the CTA. Everything above the footer is passed in so each
 * announcement keeps its own art and copy.
 */
export function AnnouncementModal({
  open,
  onDismiss,
  onSoftDismiss,
  dontShowAgainLabel,
  dontShowAgain = false,
  onDontShowAgainChange,
  reopenHintText,
  hero,
  title,
  subtitle,
  children,
  primaryCtaLabel,
  onPrimaryCta,
  primaryCtaIcon,
  secondaryCtaLabel,
  secondaryCtaIcon,
  onSecondaryCta,
  titleId,
  dismissAriaLabel,
}: AnnouncementModalProps) {
  const handleClose = () => {
    if (onSoftDismiss && !dontShowAgain) {
      onSoftDismiss();
      return;
    }
    onDismiss();
  };

  return (
    <SafeDialog
      open={open}
      onClose={handleClose}
      maxWidth="sm"
      aria-labelledby={titleId}
      slotProps={{
        backdrop: { sx: { bgcolor: "rgba(4, 17, 32, 0.55)" } },
        paper: { sx: { borderRadius: 1, p: 5, maxWidth: 550 } },
      }}
    >
      <IconButton
        aria-label={dismissAriaLabel}
        onClick={handleClose}
        sx={{ position: "absolute", top: 16, right: 16 }}
      >
        <Close />
      </IconButton>

      <DialogContent sx={{ p: 0, overflow: "visible" }}>
        <Stack>
          <Stack direction="row" spacing={3} alignItems="flex-start">
            {hero ? <Box flexShrink={0}>{hero}</Box> : null}
            {/* Copy column: the title sets the left edge everything below aligns to. */}
            <Stack spacing={1.5}>
              <Stack spacing={0.5}>
                {/* Right inset keeps the title clear of the absolutely-positioned close button. */}
                <Typography id={titleId} variant="h5" pr={3}>
                  {title}
                </Typography>
                {subtitle ? (
                  <Typography variant="subtitle1" color="text.secondary">
                    {subtitle}
                  </Typography>
                ) : null}
              </Stack>
              {children}
              {secondaryCtaLabel && onSecondaryCta ? (
                <Button
                  variant="text"
                  color="sapphire"
                  startIcon={secondaryCtaIcon}
                  onClick={onSecondaryCta}
                  sx={{ alignSelf: "flex-start", textTransform: "none", fontWeight: 400 }}
                >
                  {secondaryCtaLabel}
                </Button>
              ) : null}
            </Stack>
          </Stack>

          <Divider sx={{ my: 5 }} />

          <Stack direction="row" justifyContent="space-between" alignItems="center" spacing={2}>
            <Stack spacing={1}>
              {onSoftDismiss && dontShowAgainLabel ? (
                // Label margin zeroed so the control's own padding sets the
                // indent from the hint below, matching the mock.
                <FormControlLabel
                  control={
                    <Checkbox
                      size="small"
                      checked={dontShowAgain}
                      onChange={(event) => onDontShowAgainChange?.(event.target.checked)}
                      sx={{ mr: 1 }}
                    />
                  }
                  label={dontShowAgainLabel}
                  sx={{ ml: 0, mr: 0 }}
                />
              ) : null}
              {reopenHintText ? (
                <Typography variant="caption" color="text.secondary">
                  {reopenHintText}
                </Typography>
              ) : null}
            </Stack>
            <Button
              variant="contained"
              color="sapphire"
              onClick={onPrimaryCta}
              endIcon={primaryCtaIcon}
              sx={{ flexShrink: 0, textTransform: "none", fontSize: 16, px: 2.5 }}
            >
              {primaryCtaLabel}
            </Button>
          </Stack>
        </Stack>
      </DialogContent>
    </SafeDialog>
  );
}
